import { useEffect, useMemo, useRef, useState } from 'react';

import { useGateValue } from '@coursera-web/statsig';

import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { ORG_SCOPED_HOME_STATSIG_GATE } from 'bundles/unified-common/constants/orgScopedHomeStatsig';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useOrgScopedHomeFeature } from 'bundles/unified-common/contexts/OrgScopedHomeFeatureContext';
import { useUserExperienceContext } from 'bundles/unified-common/contexts/UserExperienceContext';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';
import pageWasServerRendered from 'bundles/unified-common/utils/pageWasServerRendered';
import {
  type MembershipsForRedirect,
  SelectionAction,
  USER_EXPERIENCE_SWITCH_PARAM,
  getUserExperienceRedirectOrSetSelection,
  performRedirectOnce,
} from 'bundles/unified-common/utils/userExperienceRedirectUtils';

export type UseUserExperienceRedirectOptions = {
  memberships?: MembershipsForRedirect | null;
  isMembershipsLoading?: boolean;
};

export type UseUserExperienceRedirectReturn = {
  /** True while resolving user experience or performing redirect; hold rendering to prevent flash. */
  isWaitingForRedirect: boolean;
};

const normalizePathname = (p: string) => (p === '' ? '/' : p);

const useUserExperienceRedirect = (options?: UseUserExperienceRedirectOptions): UseUserExperienceRedirectReturn => {
  const { pathname: routerPathname, search: routerSearch } = useLocation();

  // Route context uses router pathname so "/" and app-relative paths are correct for redirect decisions.
  const pathname = normalizePathname(routerPathname);
  // For buildRedirectUrl use browser pathname when available so path-specific overrides (/browse → org browse) see the real URL.
  const pathnameForRedirect =
    typeof window !== 'undefined' && window.location?.pathname !== undefined
      ? normalizePathname(window.location.pathname)
      : pathname;
  const search =
    typeof window !== 'undefined' && window.location?.search !== undefined
      ? window.location.search
      : routerSearch || '';
  const host = typeof window !== 'undefined' ? window.location?.host : undefined;

  const {
    userExperience,
    isLoading: isUserExperienceLoading,
    isUpdating: isUserExperienceUpdating,
    updateUserExperience,
  } = useUserExperienceContext();
  const memberships = options?.memberships;
  const isMembershipsLoading = options?.isMembershipsLoading ?? false;
  // @ts-expect-error FIXME: please avoid using the `disableExposureLog` and use qualifying events to filter exposures
  const userInOrgScopedRollout = useGateValue(ORG_SCOPED_HOME_STATSIG_GATE, { disableExposureLog: true });
  const enterpriseFeatures = useEnterpriseProductFeatures();
  const { isLoading: isOrgScopedHomeFeatureLoading } = useOrgScopedHomeFeature();
  const shouldRunRedirectLogic = useIsOrgScopedHome();
  const hasRedirectedRef = useRef(false);
  const hasSetSelectionRef = useRef(false);
  // Capture the experience-switch marker on mount so it survives URL cleanup. The cleanup effect
  // removes the param from the address bar before the redirect effect may have run (e.g. when
  // isUserExperienceLoading or isMembershipsLoading is still true on first render). Without this
  // ref, a later re-run of the redirect effect would not find the param and could bounce the user.
  const hadSwitchParamRef = useRef(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has(USER_EXPERIENCE_SWITCH_PARAM)
  );
  /*
   * This effect intentionally omits userExperience / isUpdating from its dependency array so redirect and
   * set-selection run on load and route changes only, not when the account switcher updates context.
   * Re-running this logic after a switcher-driven update would in some cases undo the user's choice and
   * cause redirect ping-pong.
   * Refs mirror the latest values so when the effect does run, it still reads up-to-date context.
   */
  const userExperienceRef = useRef(userExperience);
  userExperienceRef.current = userExperience;
  const isUserExperienceUpdatingRef = useRef(isUserExperienceUpdating);
  isUserExperienceUpdatingRef.current = isUserExperienceUpdating;
  const [isRedirectInProgress, setIsRedirectInProgress] = useState(false);

  const redirectGuard = useMemo(
    () => ({
      get: () => hasRedirectedRef.current,
      set: (value: boolean) => {
        hasRedirectedRef.current = value;
      },
    }),
    []
  );

  // Wait for the bridge provider's feature query only when the full EnterpriseProductFeaturesContext
  // isn't already available (i.e., on consumer pages). On enterprise pages where the context is loaded,
  // useIsOrgScopedHome resolves immediately and we don't need to wait for the bridge.
  const isWaitingForFeatures =
    userInOrgScopedRollout && enterpriseFeatures === undefined && isOrgScopedHomeFeatureLoading;
  const isWaitingForContext = isUserExperienceLoading || isUserExperienceUpdating || isWaitingForFeatures;
  const isRedirectDataRelevant = !!shouldRunRedirectLogic && isAuthenticatedUser();
  const isWaitingForRedirectData = isRedirectDataRelevant && isMembershipsLoading;
  // When the page hydrated from SSR, the server already resolved the redirect (or rendered the
  // page the user is meant to stay on), so blanking on the client only produces a hydration
  // mismatch and a flash. Only hold render on a full-CSR load, where there is no server content to
  // protect and holding prevents a flash before a client-side redirect. `isRedirectInProgress`
  // stays ungated so an actual redirect still holds while it navigates — it only flips true
  // post-hydration, so it never creates a mismatch.
  const isWaitingForRedirect =
    (!pageWasServerRendered() && (isWaitingForContext || isWaitingForRedirectData)) || isRedirectInProgress;

  // Clean up the experience-switch marker from the URL so it doesn't persist in the address bar.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.has(USER_EXPERIENCE_SWITCH_PARAM)) {
      params.delete(USER_EXPERIENCE_SWITCH_PARAM);
      const cleaned = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (cleaned ? `?${cleaned}` : '') + (window.location.hash || '')
      );
    }
  }, []);

  // Reset the switch guard on client-side route changes so redirect logic resumes for new navigations.
  // Uses a previous-pathname ref so the reset does NOT fire on mount (which would undo the guard).
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      hadSwitchParamRef.current = false;
      prevPathnameRef.current = pathname;
    }
  }, [pathname]);

  useEffect(() => {
    if (
      hadSwitchParamRef.current ||
      !shouldRunRedirectLogic ||
      !isAuthenticatedUser() ||
      isUserExperienceLoading ||
      isUserExperienceUpdatingRef.current ||
      isMembershipsLoading
    ) {
      return;
    }

    const result = getUserExperienceRedirectOrSetSelection(
      pathname,
      pathnameForRedirect,
      search,
      host,
      userExperienceRef.current,
      memberships
    );

    if (result?.action === SelectionAction.REDIRECT) {
      hasSetSelectionRef.current = false;
      setIsRedirectInProgress(true);
      performRedirectOnce(result.url, redirectGuard);
    } else {
      setIsRedirectInProgress(false);
      if (result?.action === SelectionAction.SET_SELECTION) {
        if (!hasSetSelectionRef.current) {
          hasSetSelectionRef.current = true;
          updateUserExperience(result.input);
        }
      } else {
        hasSetSelectionRef.current = false;
      }
    }
  }, [
    pathname,
    pathnameForRedirect,
    search,
    host,
    shouldRunRedirectLogic,
    isUserExperienceLoading,
    isMembershipsLoading,
    memberships,
    updateUserExperience,
    redirectGuard,
  ]);

  return { isWaitingForRedirect };
};

export default useUserExperienceRedirect;
